import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, MessageSquarePlus, Send, Sparkles, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtWhen } from "@/lib/api";
import { streamAssistant } from "@/lib/aiStream";

const Bubble = ({ role, content, index }) => (
  <div
    className={role === "user" ? "border-l-2 border-primary pl-3" : "border-l-2 border-border pl-3"}
    data-testid={`assistant-msg-${index}`}
  >
    <div className="font-data text-[10px] uppercase tracking-widest text-muted-foreground">
      {role === "user" ? "You asked" : "Assistant"}
    </div>
    <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed">{content}</p>
  </div>
);

export const AssistantPanel = ({ open, onClose }) => {
  const [cfg, setCfg] = useState(null);
  const [threads, setThreads] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [live, setLive] = useState("");
  const [busy, setBusy] = useState(false);
  const bottom = useRef(null);

  const loadThreads = useCallback(() => {
    api.get("/assistant/sessions").then((r) => setThreads(r.data));
  }, []);

  useEffect(() => {
    if (!open) return;
    api.get("/assistant/config").then((r) => setCfg(r.data));
    loadThreads();
  }, [open, loadThreads]);

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, live]);

  const openThread = async (id) => {
    try {
      const { data } = await api.get(`/assistant/sessions/${id}`);
      setSessionId(id);
      setMessages(data.messages);
      setLive("");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const newThread = () => {
    setSessionId(null);
    setMessages([]);
    setLive("");
    setDraft("");
  };

  const removeThread = async (id) => {
    try {
      await api.delete(`/assistant/sessions/${id}`);
      if (id === sessionId) newThread();
      loadThreads();
      toast.success("Thread removed.");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const ask = async (text) => {
    const question = (text ?? draft).trim();
    if (!question || busy) return;
    setDraft("");
    setMessages((prev) => [...prev, { role: "user", content: question }]);
    setLive("");
    setBusy(true);
    let answer = "";
    try {
      const final = await streamAssistant("/assistant/chat", { message: question, session_id: sessionId },
        (piece) => {
          answer += piece;
          setLive(answer);
        });
      setMessages((prev) => [...prev, { role: "assistant", content: answer }]);
      setLive("");
      if (final.session_id && final.session_id !== sessionId) setSessionId(final.session_id);
      loadThreads();
    } catch (err) {
      toast.error(err.message);
      setMessages((prev) => prev.slice(0, -1));
      setDraft(question);
      setLive("");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-xl flex-col border-l border-border bg-background shadow-2xl"
        data-testid="assistant-panel"
      >
        <header className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div>
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[hsl(199_89%_48%)]" />
              <span className="label-caps">Ask the register</span>
            </div>
            <h2 className="mt-1 font-display text-xl font-bold tracking-tight">
              Ask about our use cases
            </h2>
            <p className="font-data mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
              {cfg
                ? cfg.scoped_plant
                  ? `${cfg.scoped_plant} data only`
                  : "All units"
                : "Loading…"}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="assistant-new-thread"
              onClick={newThread}
              title="New thread"
              className="border border-border p-2 transition-colors duration-200 hover:bg-muted"
            >
              <MessageSquarePlus className="h-4 w-4" />
            </button>
            <button
              type="button"
              data-testid="assistant-close"
              onClick={onClose}
              className="border border-border p-2 transition-colors duration-200 hover:bg-muted"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </header>

        {threads.length > 0 && (
          <div className="border-b border-border px-5 py-3">
            <div className="label-caps mb-2">Your threads</div>
            <div className="flex max-h-24 flex-wrap gap-2 overflow-y-auto" data-testid="assistant-threads">
              {threads.map((t) => (
                <span
                  key={t.id}
                  className={`flex items-center gap-1.5 border px-2 py-1 text-[11px] ${
                    t.id === sessionId ? "border-primary bg-muted" : "border-border"
                  }`}
                >
                  <button
                    type="button"
                    data-testid={`assistant-thread-${t.id}`}
                    onClick={() => openThread(t.id)}
                    className="max-w-[220px] truncate text-left"
                    title={`${t.title} · ${fmtWhen(t.updated_at)}`}
                  >
                    {t.title}
                  </button>
                  <button
                    type="button"
                    data-testid={`assistant-thread-delete-${t.id}`}
                    onClick={() => removeThread(t.id)}
                    className="text-muted-foreground transition-colors duration-200 hover:text-[hsl(var(--at-risk))]"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
          {messages.length === 0 && !live && (
            <div data-testid="assistant-starters">
              <p className="text-sm text-muted-foreground">
                Ask in plain English. The assistant reads only this portal's register, training
                tracker and trial readings — nothing else.
              </p>
              <div className="mt-4 space-y-2">
                {(cfg?.starters || []).map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`assistant-starter-${s.slice(0, 18)}`}
                    onClick={() => ask(s)}
                    className="w-full border border-border bg-card px-3 py-2.5 text-left text-xs transition-colors duration-200 hover:bg-muted"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <Bubble key={i} index={i} role={m.role} content={m.content} />
          ))}

          {live && <Bubble index="live" role="assistant" content={live} />}
          {busy && !live && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading the register…
            </div>
          )}
          <div ref={bottom} />
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            ask();
          }}
          className="border-t border-border px-5 py-4"
        >
          <div className="flex gap-2">
            <input
              data-testid="assistant-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="e.g. which BSL use cases are stuck?"
              className="h-11 flex-1 border border-border bg-card px-3 text-sm outline-none transition-colors duration-200 focus:border-primary"
            />
            <button
              type="submit"
              disabled={busy || !draft.trim()}
              data-testid="assistant-send"
              className="flex h-11 items-center gap-2 border border-border bg-foreground px-4 font-display text-xs font-bold uppercase tracking-widest text-background transition-opacity duration-200 hover:opacity-85 disabled:opacity-40"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Ask
            </button>
          </div>
          <p className="font-data mt-2 text-[10px] uppercase tracking-wider text-muted-foreground">
            Answers come from the register — check figures before you circulate them
          </p>
        </form>
      </aside>
    </>
  );
};
